import type { ReactNode } from 'react';
import { Navigate, Route, Routes, Link, NavLink, useLocation } from 'react-router-dom';
import { useSession } from './lib/session';
import { Button, Spinner } from './components/ui';
import { ErrorBoundary } from './components/ErrorBoundary';
import {
  IconChecklist,
  IconDashboard,
  IconExport,
  IconLogout,
  IconMapping,
  IconMatch,
  IconPhoto,
  IconQuote,
  IconReport,
  IconUpload,
  IconUsers,
} from './components/icons';
import { Login } from './pages/Login';
import { ChangePassword } from './pages/ChangePassword';
import { Dashboard } from './pages/Dashboard';
import { SubmitQuote } from './pages/SubmitQuote';
import { CorrigoChecklist } from './pages/CorrigoChecklist';
import { Documents } from './pages/Documents';
import { PhotoManager } from './pages/PhotoManager';
import { DataExport } from './pages/DataExport';
import { LocationMatch } from './pages/LocationMatch';
import { PriceListMapping } from './pages/admin/PriceListMapping';
import { Reports } from './pages/admin/Reports';
import { Users } from './pages/admin/Users';

interface NavItem {
  to: string;
  label: string;
  icon: (props: { className?: string }) => ReactNode;
  admin?: boolean;
  /** Hidden from the nav but still routable (wiring kept, UI entry point removed). */
  hidden?: boolean;
}

const NAV_ITEMS: NavItem[] = [
  { to: '/', label: 'Dashboard', icon: IconDashboard },
  { to: '/submit-quote', label: 'Submit Quote', icon: IconQuote },
  { to: '/corrigo-checklist', label: 'Checklist', icon: IconChecklist },
  // Documents: superseded by the "Include PDF Copy" option on Submit Quote. The
  // route still works (/documents) but the nav entry is hidden.
  { to: '/documents', label: 'Documents', icon: IconUpload, hidden: true },
  { to: '/photo-manager', label: 'Photos', icon: IconPhoto },
  { to: '/data-export', label: 'Export', icon: IconExport, admin: true },
  { to: '/location-match', label: 'Match', icon: IconMatch, admin: true },
  { to: '/admin/price-list', label: 'Mapping', icon: IconMapping, admin: true },
  { to: '/admin/reports', label: 'Reports', icon: IconReport, admin: true },
  { to: '/admin/users', label: 'Users', icon: IconUsers, admin: true },
];

function BrandMark() {
  return (
    <Link to="/" className="flex items-center gap-2.5">
      <span className="flex h-8 w-8 items-center justify-center rounded-xl bg-gradient-to-br from-accent-500 to-accent-700 text-white shadow-sm ring-1 ring-inset ring-white/10">
        <svg
          width="19"
          height="19"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth={2}
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden
        >
          {/* Suspension-bridge mark — a nod to "Bridge". */}
          <path d="M3 17h18" />
          <path d="M8 17V6M16 17V6" />
          <path d="M3 13 8 6M21 13 16 6" />
          <path d="M8 6q4 10 8 0" />
        </svg>
      </span>
      <span className="text-[15px] font-semibold tracking-tight text-slate-900">TCG Bridge</span>
    </Link>
  );
}

function NavTab({ item }: { item: NavItem }) {
  const Icon = item.icon;
  return (
    <NavLink
      to={item.to}
      end={item.to === '/'}
      className={({ isActive }) =>
        `group inline-flex shrink-0 items-center gap-2 border-b-2 px-1 py-3 text-sm font-medium transition ${
          isActive
            ? 'border-accent-600 text-accent-700'
            : 'border-transparent text-slate-500 hover:border-slate-300 hover:text-slate-800'
        }`
      }
    >
      <Icon className="h-4 w-4" />
      {item.label}
    </NavLink>
  );
}

function Header() {
  const { user, logout } = useSession();
  if (!user) return null;

  const items = NAV_ITEMS.filter((i) => !i.hidden && (!i.admin || user.isAdmin));
  const adminItems = items.filter((i) => i.admin);
  const mainItems = items.filter((i) => !i.admin);

  return (
    <header className="sticky top-0 z-30 border-b border-slate-200 bg-white/85 backdrop-blur-md">
      <div className="mx-auto max-w-6xl px-4">
        {/* Row 1 — brand + account */}
        <div className="flex h-14 items-center justify-between gap-4">
          <BrandMark />
          <div className="flex items-center gap-2">
            <Link
              to="/change-password"
              className="hidden items-center gap-2 rounded-full py-1 pl-1 pr-3 text-sm text-slate-600 transition hover:bg-slate-100 hover:text-slate-900 sm:flex"
              title="Account & password"
            >
              <span className="flex h-7 w-7 items-center justify-center rounded-full bg-accent-100 text-xs font-semibold text-accent-700">
                {user.email.slice(0, 2).toUpperCase()}
              </span>
              <span className="max-w-[14rem] truncate">{user.email}</span>
              {user.isAdmin && (
                <span className="rounded bg-slate-100 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-slate-500">
                  Admin
                </span>
              )}
            </Link>
            <Button variant="ghost" size="sm" onClick={() => void logout()}>
              <IconLogout className="h-4 w-4" />
              <span className="hidden sm:inline">Sign out</span>
            </Button>
          </div>
        </div>
        {/* Row 2 — tool navigation */}
        <nav className="-mb-px flex items-center gap-5 overflow-x-auto">
          {mainItems.map((item) => (
            <NavTab key={item.to} item={item} />
          ))}
          {adminItems.length > 0 && (
            <>
              <span className="h-5 w-px shrink-0 bg-slate-200" aria-hidden />
              {adminItems.map((item) => (
                <NavTab key={item.to} item={item} />
              ))}
            </>
          )}
        </nav>
      </div>
    </header>
  );
}

function RequireAuth({ children }: { children: JSX.Element }) {
  const { user, loading } = useSession();
  const location = useLocation();
  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center text-accent-500">
        <Spinner className="h-6 w-6" />
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace state={{ from: location }} />;
  // A pending forced change locks the user out of everything but the
  // change-password screen (mirrors the server-side allowlist).
  if (user.mustChangePassword && location.pathname !== '/change-password') {
    return <Navigate to="/change-password" replace />;
  }
  return children;
}

function RequireAdmin({ children }: { children: JSX.Element }) {
  const { user } = useSession();
  if (!user?.isAdmin) return <Navigate to="/" replace />;
  return children;
}

export function App() {
  const { user } = useSession();
  const location = useLocation();

  return (
    <div className="flex min-h-screen flex-col">
      <Header />
      <main className="mx-auto w-full max-w-6xl flex-1 px-4 py-8">
        <ErrorBoundary key={location.pathname}>
        <div className="animate-fade-in">
          <Routes>
            <Route path="/login" element={user ? <Navigate to="/" replace /> : <Login />} />
            <Route
              path="/change-password"
              element={
                <RequireAuth>
                  <ChangePassword />
                </RequireAuth>
              }
            />
            <Route
              path="/"
              element={
                <RequireAuth>
                  <Dashboard />
                </RequireAuth>
              }
            />
            <Route
              path="/submit-quote"
              element={
                <RequireAuth>
                  <SubmitQuote />
                </RequireAuth>
              }
            />
            <Route
              path="/corrigo-checklist"
              element={
                <RequireAuth>
                  <CorrigoChecklist />
                </RequireAuth>
              }
            />
            <Route
              path="/documents"
              element={
                <RequireAuth>
                  <Documents />
                </RequireAuth>
              }
            />
            <Route
              path="/photo-manager"
              element={
                <RequireAuth>
                  <PhotoManager />
                </RequireAuth>
              }
            />
            <Route
              path="/data-export"
              element={
                <RequireAuth>
                  <RequireAdmin>
                    <DataExport />
                  </RequireAdmin>
                </RequireAuth>
              }
            />
            <Route
              path="/location-match"
              element={
                <RequireAuth>
                  <RequireAdmin>
                    <LocationMatch />
                  </RequireAdmin>
                </RequireAuth>
              }
            />
            <Route
              path="/admin/price-list"
              element={
                <RequireAuth>
                  <RequireAdmin>
                    <PriceListMapping />
                  </RequireAdmin>
                </RequireAuth>
              }
            />
            <Route
              path="/admin/reports"
              element={
                <RequireAuth>
                  <RequireAdmin>
                    <Reports />
                  </RequireAdmin>
                </RequireAuth>
              }
            />
            <Route
              path="/admin/users"
              element={
                <RequireAuth>
                  <RequireAdmin>
                    <Users />
                  </RequireAdmin>
                </RequireAuth>
              }
            />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </div>
        </ErrorBoundary>
      </main>
      {user && (
        <footer className="border-t border-slate-200/70 py-5">
          <div className="mx-auto max-w-6xl px-4 text-xs text-slate-400">
            TCG Bridge · Corrigo Pro &amp; ServiceChannel
          </div>
        </footer>
      )}
    </div>
  );
}
